const projects = [
  {
    id: 'marketplace',
    slug: 'marketplace',
    title: 'Multi-Vendor Marketplace',
    subtitle: 'Real-Time Inventory & Dynamic Pricing Engine',
    tagline: 'Build the backend brain behind a multi-seller commerce platform',
    color: '#6c63ff',
    colorGlow: 'rgba(108, 99, 255, 0.12)',
    icon: 'store',
    overview: {
      description:
        'Build a multi-vendor marketplace where sellers list products, inventory syncs across multiple sales channels in real time, and prices adjust dynamically based on demand signals. Think of the engine behind platforms like Amazon Marketplace or Flipkart — the system that ensures when a product sells on one channel, stock updates everywhere instantly, and pricing responds to market conditions automatically. Candidates build the complete multi-channel system from the ground up.',
      problem:
        'When a seller lists products across multiple channels, inventory becomes a nightmare. Sell one unit on Channel A but Channel B still shows it in stock — now you have an overbooking. Multiply this across thousands of SKUs and hundreds of sellers, and you need a system that handles concurrent updates without race conditions, prevents overselling, and adjusts prices intelligently. The challenge is building this engine from scratch.',
      outcome:
        'By the end of the week, candidates should deliver 3 apps: a Seller Portal (frontend) where sellers register, list products, manage inventory, and view orders; a Customer Storefront (frontend) where buyers browse, search, and purchase; and a Backend API that powers real-time inventory tracking across channels, concurrent stock updates, dynamic pricing, and order processing with Stripe Sandbox payments.',
    },
    aiFeatures: {
      title: 'AI-Powered Features',
      description: 'Integrate AI capabilities to make the marketplace intelligent and adaptive.',
      features: [
        {
          name: 'Smart Product Recommendations',
          detail:
            'Implement a recommendation system that suggests relevant products based on a user\'s browsing and purchase history. Analyze what similar users bought, find patterns in product categories, and surface intelligent suggestions. Even a straightforward approach using purchase co-occurrence data and product similarity scoring is valuable.',
          techHint: 'Consider purchase co-occurrence matrices, category affinity scoring, or similarity scoring on product attributes.',
        },
        {
          name: 'Demand Forecasting',
          detail:
            'Predict future demand for products based on historical sales patterns, seasonal trends, and external signals. This feeds into both inventory management (when to restock) and dynamic pricing (anticipate demand spikes). Even a straightforward approach using moving averages and trend detection on simulated data demonstrates understanding.',
          techHint: 'Consider moving averages, exponential smoothing, trend detection algorithms, or weighted historical analysis.',
        },
        {
          name: 'Fraud Detection on Orders',
          detail:
            'Flag suspicious orders using anomaly detection — unusual order quantities, rapid-fire orders from the same IP, pricing manipulation attempts. This doesn\'t need to be perfect, but showing awareness of adversarial scenarios and building even a basic anomaly scorer is impressive.',
          techHint: 'Consider statistical anomaly detection (z-scores), rule-based scoring systems, or threshold-based pattern matching.',
        },
        {
          name: 'Seller Reputation & Ranking',
          detail:
            'Build a background system that collects customer feedback and ratings on sellers, then uses those signals to influence seller visibility and ranking in search results. High-rated sellers surface first, consistently low-rated sellers get deprioritized. This runs as a background process — recalculating seller scores periodically based on recent reviews, order completion rates, and dispute history.',
          techHint: 'Weighted scoring with decay (recent reviews matter more), running averages, or composite reputation scores combining multiple signals.',
        },
      ],
    },
    integrations: [
      {
        name: 'Stripe Sandbox',
        category: 'Payments',
        description:
          'Full payment processing simulation — test credit card charges, marketplace splits (Stripe Connect for paying sellers), refunds, and subscription billing. Sandbox mode is completely free with unlimited test transactions.',
        freeDetails: 'Completely free. No limits in sandbox/test mode. Only charges on live transactions (2.9% + $0.30).',
        useCase:
          'Process customer orders, split payments between marketplace and sellers, handle refunds, and manage seller payouts.',
        links: [{ label: 'Stripe Dashboard', url: 'https://dashboard.stripe.com/register' }],
      },
      {
        name: 'Redis',
        category: 'Real-Time Cache',
        description:
          'In-memory data store for real-time inventory caching. When a product sells, update the cache instantly so all channels see accurate stock. Also useful for rate limiting, session management, distributed locks, and pub/sub for real-time notifications. Run it locally during development — it is free and open source.',
        freeDetails: 'Completely free and open source. Run locally with redis-server. For cloud deployment, Upstash offers a free tier: 256 MB, 500K commands/month.',
        useCase:
          'Cache inventory counts for instant reads, implement distributed locks to prevent overselling during concurrent updates, pub/sub for real-time stock notifications. Also serves as the broker for BullMQ job queues.',
        links: [
          { label: 'Redis Download', url: 'https://redis.io/download' },
          { label: 'Upstash (Cloud)', url: 'https://upstash.com' },
        ],
      },
      {
        name: 'Elasticsearch',
        category: 'Product Search',
        description:
          'Full-text search engine for product catalog. Supports fuzzy matching, faceted search (filter by category, price range, seller), autocomplete, and relevance scoring. Run locally via Docker — it is free and open source.',
        freeDetails: 'Completely free and open source. Run locally with Docker (docker run elasticsearch). For cloud deployment, Elastic Cloud offers a free tier: 1 deployment, 8 GB storage.',
        useCase:
          'Power product search with typo tolerance, category filtering, price range queries, and seller-specific catalogs.',
        links: [
          { label: 'Elasticsearch Download', url: 'https://www.elastic.co/downloads/elasticsearch' },
          { label: 'Elastic Cloud', url: 'https://cloud.elastic.co' },
        ],
      },
      {
        name: 'BullMQ / Celery',
        category: 'Background Jobs',
        description:
          'Distributed job queue for async processing. Handle order confirmations, inventory sync across channels, price recalculation jobs, email notifications, and scheduled tasks like daily pricing updates.',
        freeDetails: 'Open source (MIT/BSD). Completely free. BullMQ needs Redis. Celery needs Redis or RabbitMQ.',
        useCase:
          'Queue inventory sync jobs when orders arrive, schedule periodic price recalculations, send order confirmation emails asynchronously, retry failed payment webhooks.',
        links: [
          { label: 'BullMQ', url: 'https://bullmq.io' },
          { label: 'Celery', url: 'https://docs.celeryq.dev' },
        ],
      },
      {
        name: 'PostgreSQL or MongoDB',
        category: 'Data Storage',
        description:
          'Primary database for all marketplace data — sellers, products, orders, inventory records, pricing history. Choose relational (PostgreSQL) for strong consistency and complex queries, or document-based (MongoDB) for flexible schemas. Run locally, use Docker, or pick any free cloud provider.',
        freeDetails: 'Both are free and open source. Run locally or use any free hosting — Supabase, Neon, Railway, MongoDB Atlas, or any provider you prefer.',
        useCase:
          'Store product catalog, seller profiles, order history, inventory ledger, pricing rules, and audit logs.',
        links: [
          { label: 'PostgreSQL', url: 'https://www.postgresql.org' },
          { label: 'MongoDB', url: 'https://www.mongodb.com' },
        ],
      },
      {
        name: 'Socket.io / WebSocket',
        category: 'Real-Time Communication',
        description:
          'Enable real-time, bidirectional communication between server and clients. Push instant inventory updates to all connected storefronts, notify sellers when orders arrive, and broadcast price changes live without page refresh.',
        freeDetails: 'Completely free and open source. Built into Node.js natively (WebSocket) or use Socket.io library. Works with Go and Python backends as well.',
        useCase:
          'Push live inventory count updates to all storefronts when stock changes, real-time order notifications to sellers, live price ticker when dynamic pricing adjusts, instant stock alerts when items are running low.',
        links: [{ label: 'Socket.io', url: 'https://socket.io' }],
      },
      {
        name: 'Resend',
        category: 'Transactional Email',
        description:
          'Modern email API for order confirmations, shipping notifications, seller alerts, and customer communications. Clean API with great developer experience.',
        freeDetails: '3,000 emails/month, 100 emails/day, 1 domain. Permanent free tier.',
        useCase:
          'Send order confirmations to buyers, notify sellers of new orders, alert on low inventory, send pricing change summaries.',
        links: [{ label: 'Resend', url: 'https://resend.com' }],
      },
    ],
    requirements: [
      {
        title: 'Seller Portal',
        items: [
          'Seller registration with profile and store setup',
          'Product CRUD with variants (size, color), images, and descriptions',
          'Inventory management dashboard — view stock across all channels',
          'Order management — view incoming orders, update status',
          'Pricing controls — set base price, floor/ceiling, view dynamic adjustments',
          'Bulk product import capability',
        ],
      },
      {
        title: 'Customer Storefront',
        items: [
          'Product browsing with Elasticsearch-powered search and filters',
          'Product detail pages with real-time stock availability',
          'Cart and checkout with stock reservation (hold during checkout, release on timeout)',
          'Order history and tracking',
          'Smart product recommendations based on browsing and purchase patterns',
        ],
      },
      {
        title: 'Real-Time Inventory Engine',
        items: [
          'Inventory tracking per product per channel with atomic updates',
          'Concurrent update handling — two simultaneous sales should not oversell',
          'Multi-channel sync — your marketplace + 2 additional sales channels, all managed within your system',
          'Inventory audit log with full history of changes',
          'Redis-based caching and distributed locks for consistency',
        ],
      },
      {
        title: 'Dynamic Pricing System (Real-Time)',
        items: [
          'Background service that recalculates prices based on demand velocity, inventory levels, and sales patterns',
          'When price changes, push updates instantly to the Customer Storefront via WebSocket/Socket.io',
          'Base price set by seller with marketplace-level pricing rules on top',
          'Price floor/ceiling constraints to prevent runaway pricing',
          'Historical pricing data with trend visualization',
          'Demand-based adjustments — price increases when demand is high, drops when inventory sits idle',
        ],
      },
      {
        title: 'Order Processing',
        items: [
          'Cart and checkout flow with stock validation',
          'Payment processing via Stripe Sandbox',
          'Order status lifecycle (placed, confirmed, shipped, delivered)',
          'Seller notification on new orders',
          'Basic fraud detection on suspicious order patterns',
        ],
      },
      {
        title: 'Seller Feedback & Reputation',
        items: [
          'Customers can rate and review sellers after order completion',
          'Background service that periodically recalculates seller reputation scores',
          'Reputation score influences seller ranking and visibility in search results',
          'Factor in order completion rate, response time, and dispute history alongside ratings',
          'High-rated sellers surface first, low-rated sellers get deprioritized',
        ],
      },
    ],
    edgeCases: [
      'Two users buy the last item simultaneously — how do you prevent overselling?',
      'A seller updates price while a customer has the item in cart — which price wins?',
      'A simulated external channel reports a sale — how does your system sync without double-counting?',
      'What happens when Redis cache goes down — can your system still process orders?',
      'A bulk import of 10,000 products — does your system handle it without timeout?',
      'Price algorithm produces a price below cost — how do you handle safeguards?',
    ],
  },
  {
    id: 'salon',
    slug: 'salon-scheduler',
    title: 'Smart Salon Scheduler',
    subtitle: 'Intelligent Appointment & Resource Matching System',
    tagline: 'Build the booking brain behind a neighborhood salon network',
    color: '#f0c040',
    colorGlow: 'rgba(240, 192, 64, 0.12)',
    icon: 'calendar',
    overview: {
      description:
        'Build an intelligent appointment scheduling system for a salon network. Customers browse real salons (pulled from Google Places API), view practitioner profiles with ratings and reviews, and book appointments. The system manages 30-minute time slots, handles practitioner availability including lunch breaks and off-days, prevents double bookings, and matches customers with the right stylist based on expertise and reviews. All scheduling logic is built internally — external APIs are read-only data sources.',
      problem:
        'Salon booking seems simple until you factor in: multiple practitioners with different skills and schedules, overlapping appointments, cancellations that open slots, customer preferences for specific stylists, time zone edge cases, and the need to surface quality through reviews. The real challenge is building a scheduling engine that handles all these constraints while keeping the booking experience smooth and preventing conflicts.',
      outcome:
        'By the end of the week, candidates should deliver 2 apps: a Customer Booking App (frontend) where customers browse salons, view practitioner profiles and reviews, and book appointments; and a Backend API that powers the scheduling engine with 30-minute slots, availability management (opening hours, lunch breaks, off-days), intelligent stylist matching, conflict prevention, cancellations/rescheduling, notifications, and payment processing. Practitioner and admin views can be role-based sections within the same frontend.',
    },
    aiFeatures: {
      title: 'AI-Powered Features',
      description: 'Make the scheduling system smart with AI-driven capabilities.',
      features: [
        {
          name: 'Intelligent Stylist Matching',
          detail:
            'Go beyond simple availability checking. Build a matching algorithm that considers: customer preferences (past bookings, favorite stylists), stylist expertise match (coloring specialist vs. cut specialist), review sentiment analysis, workload balancing across stylists, and travel time if multiple locations. A weighted scoring system that learns from booking patterns adds real value.',
          techHint: 'Weighted scoring algorithms, preference matching, or rule-based ranking with adaptive weights.',
        },
        {
          name: 'Review Sentiment Analysis',
          detail:
            'Analyze salon and stylist reviews from Google Places API to extract quality signals. Classify reviews as positive/negative/neutral, extract specific praise or complaints (e.g., "great at coloring" vs "long wait times"), and use these signals to inform stylist rankings and recommendations.',
          techHint: 'Keyword-based scoring, dictionary-based sentiment analysis, or pattern matching on review text.',
        },
        {
          name: 'Smart Schedule Optimization',
          detail:
            'When a cancellation opens a slot, intelligently suggest that slot to customers on the waitlist based on their preferences and likelihood of booking. Predict no-show probability based on booking patterns and send targeted reminders. Optimize the daily schedule to minimize idle time between appointments.',
          techHint: 'Constraint-based scheduling, greedy slot optimization, or priority-queue based waitlist management.',
        },
        {
          name: 'Demand Prediction & Slot Pricing',
          detail:
            'Predict peak hours and days based on historical booking patterns. Optionally implement dynamic slot pricing — premium rates for high-demand times (Saturday mornings), discounts for off-peak slots (Tuesday 2 PM). Even a basic time-series analysis showing peak prediction is valuable.',
          techHint: 'Historical pattern analysis, moving averages on booking counts by hour/day, or trend detection algorithms.',
        },
      ],
    },
    integrations: [
      {
        name: 'Google Places API (Read-Only)',
        category: 'Real Salon Data',
        description:
          'Fetch real salon and barbershop data — business names, addresses, phone numbers, opening hours, ratings, customer reviews, and photos. This is READ-ONLY — you pull salon data to seed and enrich your system, but all booking and scheduling logic is internal.',
        freeDetails:
          '10,000 free requests/month (Essentials SKU). New accounts get $300 trial credit. Autocomplete requests are free — Place Details calls count against quota.',
        useCase:
          'Search for real salons by location, pull business details, display ratings and reviews, seed your database with actual salon information.',
        links: [{ label: 'Google Maps Platform', url: 'https://mapsplatform.google.com' }],
        important:
          'Read-only integration — fetch salon data, reviews, and ratings to enrich your system. All scheduling and booking is managed in your own backend.',
      },
      {
        name: 'Google Maps / OpenStreetMap',
        category: 'Geolocation & Maps',
        description:
          'Display salon locations on a map, calculate distances, enable location-based search ("salons near me"). Use Google Maps Embed API (unlimited, free) for map display, or OpenStreetMap/Nominatim for free geocoding.',
        freeDetails:
          'Google Maps Embed API: completely free, unlimited. Nominatim (OpenStreetMap): free, 1 request/sec. Google Maps Essentials: 10,000 free events/month.',
        useCase:
          'Show salon locations on an interactive map, calculate distance from customer to salon, enable "near me" search with radius filtering.',
        links: [
          { label: 'Google Maps Platform', url: 'https://mapsplatform.google.com' },
          { label: 'OpenStreetMap Nominatim', url: 'https://nominatim.openstreetmap.org' },
        ],
      },
      {
        name: 'Twilio Free Tier',
        category: 'SMS Reminders',
        description:
          'Send SMS appointment reminders, booking confirmations, and cancellation notices. Reduces no-shows significantly. Trial account includes $15 credit.',
        freeDetails:
          '$15 trial credit. 50 messages/day max. Can only send to verified numbers. Messages prefixed with trial notice.',
        useCase:
          'Send booking confirmations, 24-hour appointment reminders, cancellation confirmations, and rescheduling notifications via SMS.',
        links: [{ label: 'Twilio', url: 'https://www.twilio.com/try-twilio' }],
      },
      {
        name: 'Stripe Sandbox',
        category: 'Payment Processing',
        description:
          'Process appointment deposits and full payments. Handle cancellation refunds with configurable refund policies (full refund 24h+, 50% within 24h, no refund for no-shows). Completely free in sandbox mode.',
        freeDetails: 'Completely free in sandbox. Unlimited test transactions. Test card numbers for every scenario.',
        useCase:
          'Collect appointment deposits at booking, charge remaining amount at service completion, process refunds for cancellations based on policy, handle no-show fees.',
        links: [{ label: 'Stripe Dashboard', url: 'https://dashboard.stripe.com/register' }],
      },
      {
        name: 'PostgreSQL or MongoDB',
        category: 'Scheduling Data',
        description:
          'Store all scheduling data — practitioner profiles, availability templates, booked appointments, customer records, reviews, and pricing. PostgreSQL is recommended for scheduling due to strong date/time handling and constraint enforcement. Run locally, use Docker, or pick any free cloud provider.',
        freeDetails: 'Both are free and open source. Run locally or use any free hosting — Supabase, Neon, Railway, MongoDB Atlas, or any provider you prefer.',
        useCase:
          'Practitioner availability schedules, appointment bookings with time slot management, customer profiles and booking history, review aggregation, cancellation records.',
        links: [
          { label: 'PostgreSQL', url: 'https://www.postgresql.org' },
          { label: 'MongoDB', url: 'https://www.mongodb.com' },
        ],
      },
      {
        name: 'Resend / Brevo',
        category: 'Email Notifications',
        description:
          'Send email booking confirmations, appointment reminders, and receipts. Backup channel when SMS isn\'t available.',
        freeDetails: 'Resend: 3,000 emails/month free. Brevo: 300 emails/day free. Both permanent.',
        useCase:
          'Email booking confirmations with appointment details, send digital receipts after service, appointment reminder emails, promotional emails for off-peak slots.',
        links: [
          { label: 'Resend', url: 'https://resend.com' },
          { label: 'Brevo', url: 'https://www.brevo.com' },
        ],
      },
      {
        name: 'BullMQ / Celery',
        category: 'Background Jobs',
        description:
          'Schedule reminder jobs (send SMS 24h before appointment), process cancellation cascades, run availability recalculation, and handle periodic tasks like daily schedule generation.',
        freeDetails: 'Open source (MIT/BSD). Free. Needs Redis for BullMQ (use Upstash free tier).',
        useCase:
          'Schedule SMS/email reminders at booking time, process waitlist notifications when cancellations occur, run nightly schedule optimization, handle payment processing asynchronously.',
        links: [
          { label: 'BullMQ', url: 'https://bullmq.io' },
          { label: 'Celery', url: 'https://docs.celeryq.dev' },
        ],
      },
    ],
    requirements: [
      {
        title: 'Salon & Practitioner Data',
        items: [
          'Fetch real salon data from Google Places API (read-only) — names, locations, ratings, reviews',
          'Practitioner profiles with name, specialties, experience, and photo',
          'Availability templates — define opening hours, lunch breaks, off-days per practitioner',
          '30-minute time slot generation based on availability',
          'Reviews and ratings displayed per salon and per practitioner',
        ],
      },
      {
        title: 'Intelligent Booking Engine',
        items: [
          'Search and filter salons by location, rating, and service type',
          'Filter practitioners by rating, reviews, and expertise',
          'View available slots for selected practitioner with real-time availability',
          'Book appointment with conflict prevention (no double bookings)',
          'Stock reservation pattern — hold slot for 10 minutes during checkout',
          'AI-powered stylist matching based on preferences, ratings, and workload',
        ],
      },
      {
        title: 'Schedule Management',
        items: [
          'All scheduling logic built within your own system',
          '30-minute slot system with configurable slot duration per service',
          'Lunch breaks and off-days automatically blocked from available slots',
          'Cancellation with configurable refund policy (time-based tiers)',
          'Rescheduling flow — cancel old slot, book new one atomically',
          'Waitlist for fully-booked practitioners — notify on cancellation',
        ],
      },
      {
        title: 'Notifications & Payments',
        items: [
          'Booking confirmation via SMS (Twilio) and email (Resend)',
          'Appointment reminder 24h before via background job',
          'Deposit collection at booking via Stripe Sandbox',
          'Cancellation refund processing based on policy',
          'No-show fee handling',
        ],
      },
    ],
    edgeCases: [
      'Two customers book the same slot simultaneously — how do you prevent double booking?',
      'A practitioner calls in sick — how do you handle all their appointments for the day?',
      'Customer books a 60-minute service but you have 30-minute slots — how do you block adjacent slots?',
      'A cancellation at 3:00 PM opens a slot — is the waitlisted customer notified instantly?',
      'What happens when Google Places API is down — does your salon listing still work?',
      'A customer has a preferred stylist who is fully booked — do you suggest alternatives intelligently?',
      'Peak Saturday morning — can your system handle 50 concurrent booking attempts?',
    ],
  },
];

export const techStack = {
  title: 'Technology Stack',
  description:
    'We evaluate architectural decisions, not language choice. Pick the tools that let you build the best solution.',
  backend: {
    title: 'Backend',
    options: ['Go', 'Rust', 'Python'],
    note: 'Choose one. Each has trade-offs — we want to hear why you picked yours.',
  },
  frontend: {
    title: 'Frontend',
    options: ['React', 'Vue', 'Next.js', 'Svelte', 'or similar'],
    note: 'Flexible. A clean, functional UI is sufficient — we\'re not hiring for pixel-perfect design.',
  },
  database: {
    title: 'Database',
    options: ['PostgreSQL', 'MongoDB', 'or a combination'],
    note: 'Your choice. Relational or document-based — justify your decision based on your data model.',
  },
};

export const evaluationCriteria = [
  {
    id: 1,
    title: 'Problem Understanding',
    description:
      'Did they grasp the full scope of the problem, or did they miss critical requirements? We look for evidence that the candidate understood why certain problems are hard — not just what needs to be built, but why the naive approach fails.',
    weight: 'High',
    icon: 'brain',
  },
  {
    id: 2,
    title: 'Architectural Decisions',
    description:
      'Can they justify why they chose certain technologies, databases, patterns, and trade-offs? We don\'t want "I used PostgreSQL because a tutorial said so." We want "I chose PostgreSQL because my scheduling data has strong relational constraints and I need ACID transactions for booking atomicity."',
    weight: 'High',
    icon: 'structure',
  },
  {
    id: 3,
    title: 'Integration Quality',
    description:
      'How cleanly do the free tools connect without unnecessary complexity or redundancy? Are APIs integrated thoughtfully, or are there redundant services, unused integrations, or brittle coupling? Clean integration shows engineering maturity.',
    weight: 'Medium',
    icon: 'plug',
  },
  {
    id: 4,
    title: 'Code Organisation',
    description:
      'Is the codebase maintainable, well-structured, and documented where it matters? Clear separation of concerns, consistent patterns, meaningful naming, and documentation on non-obvious decisions. Not over-engineered, not under-documented.',
    weight: 'Medium',
    icon: 'folder',
  },
  {
    id: 5,
    title: 'Edge Cases & Testing',
    description:
      'Did they handle failures, race conditions, and unusual scenarios, or just the happy path? Concurrent booking attempts, API failures, timeout handling, data validation — these separate production-ready engineers from tutorial-followers.',
    weight: 'High',
    icon: 'shield',
  },
  {
    id: 6,
    title: 'Research Trail',
    description:
      'Can they articulate what they researched, what broke, how they debugged it? This is the most revealing dimension. It tells us whether they genuinely solved problems or just followed tutorials. We want to hear: "I tried X, it failed because Y, so I pivoted to Z." That\'s real engineering.',
    weight: 'Critical',
    icon: 'search',
  },
];

export const submission = {
  email: 'hr@zysec.ai',
  emailSubjectFormat: {
    project1: 'ProjectOne Submission - YourName',
    project2: 'ProjectTwo Submission - YourName',
  },
  requirements: [
    {
      title: 'GitHub Repository',
      detail: 'Link to your code repository. Must include a README with setup instructions, architecture overview, and any assumptions you made.',
    },
    {
      title: 'Video Walkthrough',
      detail:
        'A pre-recorded video (10-15 minutes) where you explain: what you built, your architectural choices, how you integrated each tool, what challenges you faced, and how you solved them. This is not a demo — it\'s a technical discussion.',
    },
  ],
  important:
    'The video is where we learn the most about you — invest time in it.',
};

export const roleEvaluation = {
  title: 'Roles We\'re Hiring For',
  description:
    'Based on your project choice, code depth, and how you discuss your work in the video, we assess you for roles that match your demonstrated strengths. This isn\'t a rigid matching — it emerges from how you approached the problem.',
  roles: [
    {
      title: 'Backend Engineer',
      stack: 'Python / Go / Rust',
      signals:
        'Deep focus on system design, concurrency handling, database optimization, and API architecture. Chose complex backend challenges over frontend polish. Video reveals strong systems thinking.',
    },
    {
      title: 'Full-Stack Engineer',
      stack: 'Python / Go / Rust + React / Vue / Next.js',
      signals:
        'Balanced effort between backend robustness and frontend experience. Clean API contracts, well-structured UI, and thoughtful data flow between layers. Video shows comfort across the stack.',
    },
    {
      title: 'Data & AI Engineer',
      stack: 'Python / Go / Rust',
      signals:
        'Strong focus on data modeling, pipeline design, analytics, and intelligent feature integration. Pricing algorithms, demand forecasting, or recommendation systems are well-implemented. Video reveals data-first thinking.',
    },
  ],
};

export default projects;
